import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Cookies from 'js-cookie';
import { frontendURL } from '../helper/URLHelper';

const DEFAULT_WORKSPACE_ROUTE_PATHS = {
  kanban: '/kanban',
  connections: '/connections',
  chats: '/chats',
  projects: '/projects',
  crm: '/crm',
  appointments: '/appointments',
  campaigns: '/campaigns',
  email: '/email',
  flows: '/flows',
  config_extra: '/settings',
  settings: '/settings',
};

const getWorkspaceRoutePaths = () => {
  return {
    ...DEFAULT_WORKSPACE_ROUTE_PATHS,
    ...(window.chatwootConfig?.kanbancwWorkspacePaths || {}),
  };
};

const DEFAULT_FEATURES = {
  kanbanEnabled: true,
  conexoesEnabled: true,
  chatsInternosEnabled: true,
  projectsEnabled: true,
  crmEnabled: true,
  appointmentsEnabled: true,
  disparadorEnabled: true,
  emailEnabled: true,
  chatbotFlowsEnabled: true,
};

const DEFAULT_PERMISSIONS = {
  kanbanAccess: true,
  conexoesAccess: true,
  chatsInternosAccess: true,
  projectsAccess: true,
  crmAccess: true,
  appointmentsAccess: true,
  disparadorAccess: true,
  emailAccess: true,
  chatbotFlowsAccess: true,
};

const SIDEBAR_ITEMS = [
  {
    key: 'kanban',
    menuId: 'cw-custom-menu-kanban',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.KANBAN',
    labelDefault: 'Kanban',
    icon: 'i-lucide-layout-dashboard',
    toggleName: 'cwCustomToggleKanban',
    featureKey: 'kanbanEnabled',
    permissionKey: 'kanbanAccess',
    routePath: 'kanban',
  },
  {
    key: 'conexoes',
    menuId: 'cw-custom-menu-conexoes',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.CONNECTIONS',
    labelDefault: 'Connections',
    icon: 'i-lucide-plug',
    toggleName: 'cwCustomToggleConexoes',
    featureKey: 'conexoesEnabled',
    permissionKey: 'conexoesAccess',
    routePath: 'connections',
  },
  {
    key: 'chats',
    menuId: 'cw-custom-menu-chats',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.INTERNAL_CHAT',
    labelDefault: 'Internal Chat',
    icon: 'i-lucide-messages-square',
    toggleName: 'cwCustomToggleChats',
    featureKey: 'chatsInternosEnabled',
    permissionKey: 'chatsInternosAccess',
    routePath: 'chats',
  },
  {
    key: 'projetos',
    menuId: 'cw-custom-menu-projetos',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.PROJECTS',
    labelDefault: 'Projects',
    icon: 'i-lucide-briefcase',
    toggleName: 'cwCustomToggleProjetos',
    featureKey: 'projectsEnabled',
    permissionKey: 'projectsAccess',
    routePath: 'projects',
  },
  {
    key: 'crm',
    menuId: 'cw-custom-menu-crm',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.CRM',
    labelDefault: 'CRM',
    icon: 'i-lucide-users',
    toggleName: 'cwCustomToggleCrm',
    featureKey: 'crmEnabled',
    permissionKey: 'crmAccess',
    routePath: 'crm',
  },
  {
    key: 'atendimentos',
    menuId: 'cw-custom-menu-atendimentos',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.APPOINTMENTS',
    labelDefault: 'Scheduling',
    icon: 'i-lucide-calendar-days',
    toggleName: 'cwCustomToggleAtendimentos',
    featureKey: 'appointmentsEnabled',
    permissionKey: 'appointmentsAccess',
    routePath: 'appointments',
  },
  {
    key: 'campanhas',
    menuId: 'cw-custom-menu-campanhas',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.CAMPAIGNS',
    labelDefault: 'Campaigns',
    icon: 'i-lucide-megaphone',
    toggleName: 'cwCustomToggleCampanhas',
    featureKey: 'disparadorEnabled',
    permissionKey: 'disparadorAccess',
    routePath: 'campaigns',
  },
  {
    key: 'email',
    menuId: 'cw-custom-menu-email',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.EMAIL',
    labelDefault: 'Email Marketing',
    icon: 'i-lucide-mail',
    toggleName: 'cwCustomToggleEmail',
    featureKey: 'emailEnabled',
    permissionKey: 'emailAccess',
    routePath: 'email',
  },
  {
    key: 'chatbot',
    menuId: 'cw-custom-menu-chatbot',
    labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.SMART_FLOWS',
    labelDefault: 'Smart Flows',
    icon: 'i-lucide-bot',
    toggleName: 'cwCustomToggleChatbot',
    featureKey: 'chatbotFlowsEnabled',
    permissionKey: 'chatbotFlowsAccess',
    routePath: 'flows',
  },
];

const CONFIG_EXTRA_ITEM = {
  key: 'permissions',
  menuId: 'cw-custom-menu-permissions',
  labelKey: 'WORKSPACE_BRIDGE.SIDEBAR.CONFIG_EXTRA',
  labelDefault: 'Extra Config',
  icon: 'i-lucide-settings-2',
  toggleName: 'cwCustomTogglePermissions',
  routePath: 'config_extra',
};

const SIDEBAR_ACTIVE_PREFIXES = {
  kanban: ['kanban', ''],
  conexoes: ['connections', 'conexoes'],
  chats: ['chats', 'chats-internos'],
  projetos: ['projects', 'projects-reports'],
  crm: ['crm'],
  atendimentos: ['appointments', 'atendimentos'],
  campanhas: ['campaigns', 'disparador'],
  email: ['email', 'email-marketing'],
  chatbot: ['flows', 'chatbot'],
  permissions: [
    'settings',
    'permissions',
    'admin/setup',
    'admin/permissions',
    'admin/scripts',
    'global-settings',
    'config-globais',
    'webhook-configs',
    'webhooks',
  ],
};

const sidebarAccessCache = new Map();

const getWorkspacePublicUrl = () =>
  window.chatwootConfig?.kanbancwWorkspaceUrl || '';

const buildWorkspaceShellPath = (routePath, accountId = null) => {
  if (!accountId) return null;

  const workspaceRoutePaths = getWorkspaceRoutePaths();
  const resolvedRoutePath = workspaceRoutePaths[routePath] || routePath;
  const normalizedPath = String(resolvedRoutePath || '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');

  const shellPath = normalizedPath
    ? `accounts/${accountId}/workspace/${normalizedPath}`
    : `accounts/${accountId}/workspace`;

  return frontendURL(shellPath);
};

const resolveRouteParamValue = value => {
  if (Array.isArray(value)) return value[0];
  return value;
};

const extractConversationIdFromPath = path => {
  const match = String(path || '').match(/\/conversations\/(\d+)/);
  return match?.[1] || null;
};

const normalizeWorkspaceRouteSegment = value => {
  if (Array.isArray(value)) {
    return value.join('/').replace(/^\/+/, '').replace(/\/+$/, '');
  }

  return String(value || '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
};

const matchesWorkspacePrefix = (currentSegment, prefix) => {
  const normalizedCurrent = normalizeWorkspaceRouteSegment(currentSegment);
  const normalizedPrefix = normalizeWorkspaceRouteSegment(prefix);

  if (!normalizedPrefix) {
    return normalizedCurrent === '';
  }

  return (
    normalizedCurrent === normalizedPrefix ||
    normalizedCurrent.startsWith(`${normalizedPrefix}/`)
  );
};

const getAuthHeaders = () => {
  try {
    const raw = Cookies.get('cw_d_session_info');
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.['access-token']) return null;

    return {
      'access-token': session['access-token'],
      'token-type': session['token-type'] || 'Bearer',
      client: session.client || '',
      expiry: session.expiry || '',
      uid: session.uid || '',
    };
  } catch (_) {
    return null;
  }
};

const fetchBridgeJson = async (path, accountId = null) => {
  const baseUrl = getWorkspacePublicUrl();
  const authHeaders = getAuthHeaders();

  if (!baseUrl || !authHeaders) {
    throw new Error('missing-bridge-config');
  }

  const headers = {
    ...authHeaders,
  };

  if (accountId) {
    headers['X-Account-ID'] = String(accountId);
  }

  const response = await fetch(`${baseUrl}${path}`, {
    headers,
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`bridge-request-failed:${response.status}`);
  }

  return response.json();
};

const buildVisibleItems = ({ features, permissions, configExtraRoutePath }) => {
  const visibleItems = SIDEBAR_ITEMS.filter(item => {
    return (
      features[item.featureKey] !== false &&
      permissions[item.permissionKey] !== false
    );
  });

  if (configExtraRoutePath) {
    visibleItems.push({
      ...CONFIG_EXTRA_ITEM,
      routePath: configExtraRoutePath,
    });
  }

  return visibleItems;
};

const resolveConfigExtraRoutePath = access => {
  if (access.isPlatformSuperAdmin || access.isTenantAdmin) {
    return 'config_extra';
  }

  return null;
};

export function useKanbanCWBridge() {
  const router = useRouter();
  const route = useRoute();
  const workspacePublicUrl = computed(() => getWorkspacePublicUrl());
  const hasKanbanCWBridge = computed(() => Boolean(workspacePublicUrl.value));

  const fetchSidebarItems = async accountId => {
    if (!hasKanbanCWBridge.value || !accountId) return [];

    const cacheKey = String(accountId);
    if (sidebarAccessCache.has(cacheKey)) {
      return sidebarAccessCache.get(cacheKey);
    }

    try {
      const [featuresResult, permissionsResult, accessResult] =
        await Promise.all([
          fetchBridgeJson('/api/features', accountId),
          fetchBridgeJson('/api/permissions/me', accountId),
          fetchBridgeJson('/api/admin/my-access', accountId),
        ]);

      const features = { ...DEFAULT_FEATURES, ...(featuresResult?.data || {}) };
      const permissions = {
        ...DEFAULT_PERMISSIONS,
        ...(permissionsResult?.data || {}),
      };
      const access = accessResult?.data || {};
      const configExtraRoutePath = resolveConfigExtraRoutePath(access);
      const items = buildVisibleItems({
        features,
        permissions,
        configExtraRoutePath,
      });

      sidebarAccessCache.set(cacheKey, items);
      return items;
    } catch (_) {
      const items = buildVisibleItems({
        features: DEFAULT_FEATURES,
        permissions: DEFAULT_PERMISSIONS,
        configExtraRoutePath: null,
      });
      sidebarAccessCache.set(cacheKey, items);
      return items;
    }
  };

  const openSidebarItem = (itemOrKey, accountId = null) => {
    const item =
      typeof itemOrKey === 'string'
        ? [...SIDEBAR_ITEMS, CONFIG_EXTRA_ITEM].find(
            entry => entry.key === itemOrKey
          )
        : itemOrKey;

    const resolvedAccountId = accountId || route.params?.accountId || null;

    const workspaceShellPath = item?.routePath
      ? buildWorkspaceShellPath(item.routePath, resolvedAccountId)
      : null;

    if (workspaceShellPath) {
      router.push(workspaceShellPath);
      return;
    }

    const action = item?.toggleName ? window[item.toggleName] : null;
    if (typeof action === 'function') {
      action();
    }
  };

  const resolveActiveSidebarItem = currentRoute => {
    const routeToResolve = currentRoute || route;
    if (routeToResolve?.name !== 'workspace_shell') {
      return null;
    }

    const currentSegment = normalizeWorkspaceRouteSegment(
      routeToResolve.params?.workspacePath
    );

    return (
      [...SIDEBAR_ITEMS, CONFIG_EXTRA_ITEM].find(item => {
        const expectedPath = normalizeWorkspaceRouteSegment(
          getWorkspaceRoutePaths()[item.routePath] || item.routePath
        );
        const activePrefixes = [
          expectedPath,
          ...(SIDEBAR_ACTIVE_PREFIXES[item.key] || []),
        ];

        return activePrefixes.some(prefix =>
          matchesWorkspacePrefix(currentSegment, prefix)
        );
      }) || null
    );
  };

  const openConversationWidget = () => {
    const conversationId =
      resolveRouteParamValue(route.params?.conversation_id) ||
      resolveRouteParamValue(route.params?.conversationId) ||
      extractConversationIdFromPath(route.fullPath);

    const resolvedAccountId = route.params?.accountId || null;
    const workspaceShellPath = conversationId
      ? buildWorkspaceShellPath(
          `conversation/${conversationId}`,
          resolvedAccountId
        )
      : null;

    if (workspaceShellPath) {
      router.push(workspaceShellPath);
    }
  };

  const onPanelStateChange = handler => {
    window.addEventListener('kbcw:panel-state', handler);
    return () => window.removeEventListener('kbcw:panel-state', handler);
  };

  const onConversationStageChange = handler => {
    window.addEventListener('kbcw:conversation-stage', handler);
    return () => window.removeEventListener('kbcw:conversation-stage', handler);
  };

  return {
    hasKanbanCWBridge,
    fetchSidebarItems,
    openSidebarItem,
    resolveActiveSidebarItem,
    openConversationWidget,
    onPanelStateChange,
    onConversationStageChange,
  };
}
